// @boty/database - Intake Responses Query Utilities (Story 7.6)

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, IntakeResponseInsert, IntakeSessionInsert } from '../types';

type DbClient = SupabaseClient<Database>;

// ============================================================================
// INTAKE RESPONSES QUERIES
// ============================================================================

/**
 * Insert or update a single intake response.
 * Deletes any existing response for the same session+question first,
 * then inserts the new one. This handles re-answering via Back button.
 */
export async function createIntakeResponse(
  client: DbClient,
  data: IntakeResponseInsert,
) {
  // Remove previous response for this session+question (if re-answering via Back)
  if (data.intake_question_id) {
    await client
      .from('intake_responses')
      .delete()
      .eq('bot_id', data.bot_id)
      .eq('session_id', data.session_id)
      .eq('intake_question_id', data.intake_question_id);
  }

  const { data: result, error } = await client
    .from('intake_responses')
    .insert(data)
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Get all intake responses for a bot grouped by session.
 * Returns responses with their associated question text.
 */
export async function getIntakeResponsesByBot(
  client: DbClient,
  botId: string,
  page: number = 1,
  pageSize: number = 20,
) {
  // Get distinct sessions with pagination
  const offset = (page - 1) * pageSize;

  const { data: sessions, error: sessionsError, count } = await client
    .from('intake_sessions')
    .select('*', { count: 'exact' })
    .eq('bot_id', botId)
    .order('created_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (sessionsError) throw sessionsError;
  if (!sessions || sessions.length === 0) {
    return { data: [], total: 0 };
  }

  const sessionIds = sessions.map((s) => s.session_id);

  // Get all responses for these sessions
  const { data: responses, error: responsesError } = await client
    .from('intake_responses')
    .select('*, intake_questions(question_text)')
    .eq('bot_id', botId)
    .in('session_id', sessionIds)
    .order('created_at', { ascending: true });

  if (responsesError) throw responsesError;

  // Group by session
  const grouped = sessions.map((session) => ({
    sessionId: session.session_id,
    confirmedAt: session.confirmed_at,
    notifiedAt: session.notified_at,
    createdAt: session.created_at,
    responses: (responses || []).filter((r) => r.session_id === session.session_id),
  }));

  return { data: grouped, total: count || 0 };
}

// ============================================================================
// INTAKE SESSIONS QUERIES
// ============================================================================

/**
 * Upsert an intake session (create or update).
 */
export async function upsertIntakeSession(
  client: DbClient,
  data: IntakeSessionInsert,
) {
  const { data: result, error } = await client
    .from('intake_sessions')
    .upsert(data, { onConflict: 'bot_id,session_id' })
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Mark an intake session as confirmed (consumer said "yes, send my info").
 */
export async function confirmIntakeSession(
  client: DbClient,
  botId: string,
  sessionId: string,
) {
  // Only set confirmed_at if not already confirmed (idempotent)
  const { data: existing, error: fetchError } = await client
    .from('intake_sessions')
    .select('*')
    .eq('bot_id', botId)
    .eq('session_id', sessionId)
    .single();

  if (fetchError) throw fetchError;

  if (existing.confirmed_at) {
    return existing; // Already confirmed, return as-is
  }

  const { data, error } = await client
    .from('intake_sessions')
    .update({
      confirmed_at: new Date().toISOString(),
    })
    .eq('bot_id', botId)
    .eq('session_id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Mark an intake session as notified (prevent duplicate notifications).
 */
export async function markIntakeSessionNotified(
  client: DbClient,
  botId: string,
  sessionId: string,
) {
  const { error } = await client
    .from('intake_sessions')
    .update({
      notified_at: new Date().toISOString(),
    })
    .eq('bot_id', botId)
    .eq('session_id', sessionId);

  if (error) throw error;
}

/**
 * Get pending intake sessions for timeout notification (1-hour timeout).
 * Returns sessions where:
 * - notified_at IS NULL
 * - confirmed_at IS NULL
 * - last_response_at < NOW() - 1 hour
 */
export async function getPendingIntakeTimeouts(client: DbClient) {
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

  const { data, error } = await client
    .from('intake_sessions')
    .select('*, bots(id, name, user_id, slug)')
    .is('notified_at', null)
    .is('confirmed_at', null)
    .lt('last_response_at', oneHourAgo)
    .not('last_response_at', 'is', null)
    .limit(100);

  if (error) throw error;
  return data || [];
}

/**
 * Get intake responses for a specific session (for notification content).
 */
export async function getIntakeResponsesForSession(
  client: DbClient,
  botId: string,
  sessionId: string,
) {
  const { data, error } = await client
    .from('intake_responses')
    .select('*, intake_questions(question_text)')
    .eq('bot_id', botId)
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}
